import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook for managing the current user's block list.
 * Exposes a Set of blocked user IDs and helpers.
 */
export function useBlockedUsers() {
  const { user } = useAuth();
  const [blockedIds, setBlockedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    if (!user) {
      setBlockedIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('blocked_users')
        .select('blocked_id')
        .eq('blocker_id', user.id);
      if (error) throw error;
      setBlockedIds(new Set((data || []).map((r: { blocked_id: string }) => r.blocked_id)));
    } catch (e) {
      console.error('[useBlockedUsers] refresh failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const block = async (targetId: string) => {
    if (!user) return false;
    const { error } = await supabase
      .from('blocked_users')
      .insert({ blocker_id: user.id, blocked_id: targetId });
    if (error) {
      console.error('[useBlockedUsers] block failed', error);
      return false;
    }
    setBlockedIds((prev) => new Set(prev).add(targetId));
    return true;
  };

  const unblock = async (targetId: string) => {
    if (!user) return false;
    const { error } = await supabase
      .from('blocked_users')
      .delete()
      .eq('blocker_id', user.id)
      .eq('blocked_id', targetId);
    if (error) {
      console.error('[useBlockedUsers] unblock failed', error);
      return false;
    }
    setBlockedIds((prev) => {
      const next = new Set(prev);
      next.delete(targetId);
      return next;
    });
    return true;
  };

  return {
    blockedIds,
    loading,
    refresh,
    block,
    unblock,
    isBlocked: (id: string) => blockedIds.has(id),
  };
}
